<script setup lang="ts">
import type { CustomPricingBase } from "@classes-service-types/index.ts";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { useCreateCustomPricing } from "@/providers/customPricing/useCreateCustomPricing";
import { useGetCustomPricingOne } from "@/providers/customPricing/useGetCustomPricingOne";
import { useUpdateCustomPricing } from "@/providers/customPricing/useUpdateCustomPricing";
import { centsToDollars } from "../utils";

defineOptions({ name: "CreateEditModal" });

const props = withDefaults(
  defineProps<{
    visible: boolean;
    overrideId?: string;
  }>(),
  {
    visible: false,
    overrideId: undefined,
  },
);

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
  (e: "saved"): void;
}>();

const toast = useToast();

const isEditMode = computed<boolean>(() => Boolean(props.overrideId));
const overrideIdRef = computed<string | undefined>(() => props.overrideId);

const { data: existingData, isLoading: isLoadingExisting } = useGetCustomPricingOne(overrideIdRef, {
  enabled: computed(() => Boolean(overrideIdRef.value)),
});

// Form state
const sessionId = ref<string>("");
const homeInstitutionId = ref<string>("");
const sessionName = ref<string | undefined>(undefined);
const institutionName = ref<string | undefined>(undefined);
const title = ref<string>("");
// Form displays dollars (staff think in dollars); we convert ÷100 on load. Price editing is frozen —
// the inputs are disabled and `buildPayload` omits the price keys. Re-add the dollars→cents conversion
// and the payload keys when the freeze lifts. The API contract stays in cents.
const internshipsPriceInDollars = ref<number | undefined>(undefined);
const classAcademicFeesPriceInDollars = ref<number | undefined>(undefined);

// Populate form when editing
watch(
  () => existingData.value,
  (data) => {
    if (!data) {
      return;
    }
    const record = data.data;
    sessionId.value = record.session_id ?? "";
    homeInstitutionId.value = record.home_institution_id ?? "";
    sessionName.value = record.session_name;
    institutionName.value = record.home_institution_name;
    title.value = record.title ?? "";
    internshipsPriceInDollars.value = centsToDollars(record.internships_price_in_cents);
    classAcademicFeesPriceInDollars.value = centsToDollars(record.class_academic_fees_price_in_cents);
  },
  { immediate: true },
);

// Reset form when switching to create mode
watch(
  () => props.overrideId,
  (newId) => {
    if (!newId) {
      sessionId.value = "";
      homeInstitutionId.value = "";
      sessionName.value = undefined;
      institutionName.value = undefined;
      title.value = "";
      internshipsPriceInDollars.value = undefined;
      classAcademicFeesPriceInDollars.value = undefined;
    }
  },
);

const { mutate: createOverride, isPending: isCreating } = useCreateCustomPricing();
const { mutate: updateOverride, isPending: isUpdating } = useUpdateCustomPricing();

const isSaving = computed<boolean>(() => isCreating.value || isUpdating.value);

const buildPayload = (): CustomPricingBase => ({
  session_id: sessionId.value,
  home_institution_id: homeInstitutionId.value,
  title: title.value || "",
});

const handleSave = (): void => {
  const payload = buildPayload();

  if (isEditMode.value && props.overrideId) {
    updateOverride(
      { overrideId: props.overrideId, data: payload },
      {
        onSuccess: () => {
          toast.add({
            severity: "success",
            summary: "Custom Pricing",
            detail: "Override updated successfully.",
            group: "br-ce",
            life: 5000,
          });
          emit("saved");
          emit("update:visible", false);
        },
        onError: (error: Error & { cause?: { data?: { details?: { detail?: { message?: string } } } } }) => {
          toast.add({
            severity: "error",
            summary: "Custom Pricing",
            detail: `Failed to update override. ${error?.cause?.data?.details?.detail?.message || error.message}`,
            group: "br-ce",
            life: 15000,
          });
        },
      },
    );
  } else {
    createOverride(
      { data: payload },
      {
        onSuccess: () => {
          toast.add({
            severity: "success",
            summary: "Custom Pricing",
            detail: "Override created successfully.",
            group: "br-ce",
            life: 5000,
          });
          emit("saved");
          emit("update:visible", false);
        },
        onError: (error: Error & { cause?: { data?: { details?: { detail?: { message?: string } } } } }) => {
          toast.add({
            severity: "error",
            summary: "Custom Pricing",
            detail: `Failed to create override. ${error?.cause?.data?.details?.detail?.message || error.message}`,
            group: "br-ce",
            life: 15000,
          });
        },
      },
    );
  }
};

const handleHide = (): void => {
  emit("update:visible", false);
};
</script>

<template>
  <Toast position="bottom-right" group="br-ce" />
  <Dialog
    :visible="visible"
    @update:visible="handleHide"
    :header="isEditMode ? 'Edit Custom Pricing Override' : 'Create Custom Pricing Override'"
    :modal="true"
    :closable="true"
    :style="{ width: '540px' }"
  >
    <div v-if="isLoadingExisting && isEditMode" class="flex justify-center py-8">
      <span class="material-symbols-outlined animate-spin">progress_activity</span>
    </div>

    <div v-else class="flex flex-col gap-4 py-2">
      <!-- Edit mode: show names as read-only context -->
      <template v-if="isEditMode">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold uppercase tracking-wide text-gray-400">Session Name</label>
          <div class="text-sm text-gray-800">{{ sessionName ?? sessionId }}</div>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold uppercase tracking-wide text-gray-400">Institution</label>
          <div class="text-sm text-gray-800">{{ institutionName ?? homeInstitutionId }}</div>
        </div>
      </template>

      <!-- Create mode: accept IDs as inputs -->
      <template v-else>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-semibold text-gray-700">Session ID <span class="text-red-500">*</span></label>
          <InputText
            v-model="sessionId"
            placeholder="Session UUID"
            class="w-full"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-semibold text-gray-700">Home Institution ID <span class="text-red-500">*</span></label>
          <InputText
            v-model="homeInstitutionId"
            placeholder="Institution ID"
            class="w-full"
          />
        </div>
      </template>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-semibold text-gray-700">Scholarship Title</label>
        <InputText
          v-model="title"
          placeholder="Override title (optional)"
          class="w-full"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-semibold text-gray-700">Internship Price</label>
        <InputNumber
          v-model="internshipsPriceInDollars"
          placeholder="$1,500.00"
          :min="0"
          mode="currency"
          currency="USD"
          locale="en-US"
          :min-fraction-digits="2"
          :max-fraction-digits="2"
          class="w-full"
          :disabled="true"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-semibold text-gray-700">Class Price</label>
        <InputNumber
          v-model="classAcademicFeesPriceInDollars"
          placeholder="$750.00"
          :min="0"
          mode="currency"
          currency="USD"
          locale="en-US"
          :min-fraction-digits="2"
          :max-fraction-digits="2"
          class="w-full"
          :disabled="true"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Cancel"
          severity="secondary"
          @click="handleHide"
          :disabled="isSaving"
        />
        <Button
          :label="isEditMode ? 'Update' : 'Create'"
          @click="handleSave"
          :loading="isSaving"
          :disabled="!sessionId || !homeInstitutionId"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
</style>
